import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useDirectoryClients, useImportDirectoryClients } from '../../directory/api';
import { useCreateClient } from '../api';
import { ClientFormModal } from './ClientFormModal';

/**
 * THE way a client is created — /clients' "New client" and the invoice
 * builder's "+ New client" are this one component, so a client made while
 * raising an invoice is made exactly like one made on the list: from an
 * Accounts organization through the directory import (a same-name client is
 * LINKED, never duplicated), or by hand.
 *
 * Extracted from `ClientsPage` (Jesse, 2026-09-30: *"select a client (or new
 * client)"*) rather than copied — two create paths is how one of them stops
 * linking to Accounts.
 */
export function NewClientModal({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The id of the client that now exists — created or linked. */
  onCreated: (clientId: string) => void;
}) {
  const queryClient = useQueryClient();
  const accountsClients = useDirectoryClients();
  const importClients = useImportDirectoryClients();
  const createClient = useCreateClient();

  /** Every picker that lists clients, not just /clients. */
  const refreshPickers = () => {
    void queryClient.invalidateQueries({ queryKey: ['invoices', 'clients'] });
    void queryClient.invalidateQueries({ queryKey: ['agreements', 'clients'] });
  };

  return (
    <ClientFormModal
      open={open}
      onOpenChange={onOpenChange}
      title="New client"
      submitLabel="Create client"
      submitting={createClient.isPending || importClients.isPending}
      accounts={{
        rows: accountsClients.data?.ok ? accountsClients.data.rows : [],
        loading: accountsClients.isLoading,
        unavailable: accountsClients.isError
          ? 'the directory could not be read'
          : accountsClients.data && !accountsClients.data.ok
            ? (accountsClients.data.reason ?? 'the directory is not connected')
            : null,
      }}
      onSubmit={async (values) => {
        if (values.accountsOrg) {
          // ONE path makes a client from an Accounts org — the directory's
          // import — so a same-name client is linked, never duplicated, and
          // the hub project import finds it afterwards. The bill-to details
          // typed here are applied on top of whichever client that yields.
          const result = await importClients.mutateAsync([values.accountsOrg.orgId]);
          const id = result.created[0]?.clientId ?? result.linked[0]?.clientId ?? null;
          if (!result.ok || !id) {
            throw new Error(
              result.reason ??
                (result.refused.length > 0
                  ? 'Accounts no longer offers that organization — reload and try again.'
                  : 'That organization is already a client here.'),
            );
          }
          const billing = Object.fromEntries(
            Object.entries(values.billing).filter(([, v]) => v !== null && v !== ''),
          );
          if (values.notes || Object.keys(billing).length > 0) {
            await api.patch(`/clients/${id}`, {
              ...billing,
              ...(values.notes ? { notes: values.notes } : {}),
            });
          }
          refreshPickers();
          onCreated(id);
          return;
        }
        const created = await createClient.mutateAsync({
          name: values.name,
          notes: values.notes || null,
          ...values.billing,
        });
        refreshPickers();
        onCreated(created.id);
      }}
    />
  );
}
